import React, { useEffect, useMemo, useState } from "react";
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    MenuItem,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { useSnackbar } from "notistack";
import erpInventoryServices from "app/services/erp-inventory-services";
import warehouseBinServices from "app/services/warehouse-bin-services";

// Asignar la posición de un producto cuando el ERP no la entrega.
//
// Es por bodega: un producto que vive en tres bodegas tiene tres posiciones
// distintas, porque cada bodega tiene sus propios racks.
const SetPositionDialog = ({ open, onClose, product, centres, onSaved }) => {
    const { enqueueSnackbar } = useSnackbar();

    const [centreId, setCentreId] = useState("");
    const [binInput, setBinInput] = useState("");
    const [options, setOptions] = useState([]);
    const [searching, setSearching] = useState(false);
    const [saving, setSaving] = useState(false);

    // Las bodegas donde el producto efectivamente tiene stock o presencia. No
    // tiene sentido ofrecer una bodega donde el producto no existe.
    const availableCentres = useMemo(() => {
        const present = (product?.warehouses || []).map((w) => w.centre_id).filter(Boolean);
        if (!present.length) return centres;
        return centres.filter((centre) => present.includes(centre.id));
    }, [centres, product]);

    useEffect(() => {
        if (!open) return;
        setBinInput("");
        setOptions([]);
        setCentreId(availableCentres.length === 1 ? availableCentres[0].id : "");
    }, [open, availableCentres]);

    useEffect(() => {
        if (!binInput || binInput.length < 1 || !centreId) {
            setOptions([]);
            return undefined;
        }

        const timer = setTimeout(async () => {
            try {
                setSearching(true);
                const data = await warehouseBinServices.getBins(
                    `?centre_id=${centreId}&q=${encodeURIComponent(binInput)}&per_page=20`
                );
                setOptions(data?.data || []);
            } catch (_error) {
                setOptions([]);
            } finally {
                setSearching(false);
            }
        }, 350);

        return () => clearTimeout(timer);
    }, [binInput, centreId]);

    const currentForCentre = useMemo(
        () => (product?.warehouses || []).find((w) => w.centre_id === centreId),
        [product, centreId]
    );

    const save = async (value) => {
        const binCode = typeof value === "string" ? value : value?.code;
        if (!binCode?.trim() || !centreId) return;

        try {
            setSaving(true);
            const result = await erpInventoryServices.setProductBin({
                product_code: product.code,
                centre_id: centreId,
                bin_code: binCode.trim(),
            });
            enqueueSnackbar(result.message || "Posición guardada", { variant: "success" });
            onSaved();
            onClose();
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.error || "No se pudo guardar", { variant: "error" });
        } finally {
            setSaving(false);
        }
    };

    const clear = async () => {
        try {
            setSaving(true);
            await erpInventoryServices.setProductBin({ product_code: product.code, centre_id: centreId });
            enqueueSnackbar("Posición quitada", { variant: "success" });
            onSaved();
            onClose();
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.error || "No se pudo quitar", { variant: "error" });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                Posición de {product?.code}
                <Typography variant="body2" color="text.secondary">{product?.name}</Typography>
            </DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    <TextField
                        select
                        fullWidth
                        size="small"
                        label="Bodega"
                        value={centreId}
                        onChange={(event) => setCentreId(event.target.value)}
                        helperText="Cada bodega tiene sus propios racks, así que la posición es por bodega"
                    >
                        {availableCentres.map((centre) => (
                            <MenuItem key={centre.id} value={centre.id}>{centre.name}</MenuItem>
                        ))}
                    </TextField>

                    {currentForCentre && (
                        <Box>
                            <Typography variant="caption" color="text.secondary" display="block">
                                Ubicación que reporta el ERP: {currentForCentre.location || "ninguna"}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                                Posición actual: {currentForCentre.bin_code || "sin asignar"}
                            </Typography>
                        </Box>
                    )}

                    <Autocomplete
                        freeSolo
                        options={options}
                        loading={searching}
                        disabled={!centreId || saving}
                        inputValue={binInput}
                        getOptionLabel={(option) => (typeof option === "string" ? option : option.code || "")}
                        filterOptions={(opts) => opts}
                        onInputChange={(_event, value) => setBinInput(value)}
                        onChange={(_event, value) => value && save(value)}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                size="small"
                                label="Posición"
                                placeholder="A-01-03-B-02"
                                onKeyDown={(event) => {
                                    if (event.key === "Enter" && binInput.trim()) {
                                        event.preventDefault();
                                        save(binInput);
                                    }
                                }}
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {(searching || saving) && <CircularProgress size={18} />}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                }}
                            />
                        )}
                    />

                    <Alert severity="info" sx={{ py: 0.5 }}>
                        <Typography variant="body2">
                            Si el código no existe se crea. Lo que asignes acá manda sobre lo que diga el ERP y no
                            se sobrescribe en la sincronización.
                        </Typography>
                    </Alert>
                </Stack>
            </DialogContent>
            <DialogActions>
                {currentForCentre?.bin_code && (
                    <Button color="error" onClick={clear} disabled={saving}>Quitar</Button>
                )}
                <Box sx={{ flexGrow: 1 }} />
                <Button onClick={onClose}>Cancelar</Button>
                <LoadingButton variant="contained" loading={saving} disabled={!binInput.trim() || !centreId}
                               onClick={() => save(binInput)}>
                    Guardar
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
};

export default SetPositionDialog;
